import { MaxLengthValidatorDirective } from '../../shared/directives/max-length-validator.directive';
import { BusinessClock } from '../../core/time/business-clock.service';
import { clockView } from '../../core/time/business-time';
import { businessDayjs } from '../../core/time/business-calendar';
import { jstCalendarPlugin, calendarNow } from '../../core/time/business-calendar';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import jaLocale from '@fullcalendar/core/locales/ja';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import 'dayjs/locale/ja';
import { PageTitleService } from '../../core/services/page-title.service';
import { PtReservationService } from '../../core/services/pt-reservation.service';
import {
  JAPANESE_HOLIDAY_SOURCE_ID,
  loadJapaneseHolidayEvents,
} from '../../shared/utils/japanese-holiday-calendar';

dayjs.extend(isSameOrAfter);
dayjs.locale('ja');

@Component({
    selector: 'app-pt-reservation',
    imports: [MaxLengthValidatorDirective, FormsModule],
    templateUrl: './pt-reservation.component.html'
})
export class PtReservationComponent implements OnInit, OnDestroy {
  private readonly businessClock = inject(BusinessClock);
  private readonly clockContext = clockView(true);

  private pageTitle = inject(PageTitleService);
  private ptReservation = inject(PtReservationService);
  private toastr = inject(ToastrService);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild('calendarElement') private calendarElement?: ElementRef<HTMLElement>;

  reservationGlobalFlag = false;
  items: any[] | null = null;
  menuItems: any[] = [];
  showTable = false;

  modalControl: any = {};
  selectReservation: any = {};

  pageLimit = 10;
  limitBegin = 0;
  columnWidth = 11;
  showStart = 0;
  showEnd = 10;
  keyword = '';

  private calendar: Calendar | null = null;
  private holidayLoadId = 0;

  async ngOnInit(): Promise<void> {
    this.pageTitle.set('fa fa-calendar', 'パーソナルトレーニング予約');
    this.reservationGlobalFlag = await this.ptReservation.getGlobalFlag();
    await this.update();
  }

  ngOnDestroy(): void {
    this.destroyCalendar();
  }

  async update(): Promise<void> {
    const data = await this.ptReservation.getAll();
    this.items = data.map((item: any) => ({
      ...item,
      formattedStartedAt: businessDayjs(item.startedAt).format('YYYY/MM/DD(ddd) HH:mm'),
    }));
    this.showTable = true;
    this.pagerClick(0);
  }

  get filteredItems(): any[] {
    if (!this.items) return [];
    if (!this.keyword) return this.items;
    const kw = this.keyword.toLowerCase();
    return this.items.filter(item => JSON.stringify(item).toLowerCase().includes(kw));
  }

  get pagedItems(): any[] {
    return this.filteredItems.slice(this.limitBegin, this.limitBegin + this.pageLimit);
  }

  get pageCount(): number {
    return Math.ceil(this.filteredItems.length / this.pageLimit);
  }

  get pagerPages(): number[] {
    return Array.from({ length: this.pageCount }, (_, i) => i);
  }

  get currentPage(): number {
    return Math.floor(this.limitBegin / this.pageLimit);
  }

  pagerClick(num: number): void {
    num = Math.ceil(num);
    const total = this.pageCount;
    if (num + 1 <= Math.floor(this.columnWidth / 2)) {
      this.showStart = 0;
      this.showEnd = this.columnWidth - 1;
    } else if (num + 1 > total - Math.floor(this.columnWidth / 2)) {
      this.showStart = total - this.columnWidth;
      this.showEnd = total;
    } else {
      this.showStart = num - Math.floor(this.columnWidth / 2);
      this.showEnd = num + Math.floor(this.columnWidth / 2);
    }
    this.limitBegin = num * this.pageLimit;
  }

  async initNewReserve(): Promise<void> {
    this.modalControl = { isSelectMenu: true };
    this.selectReservation = {};
    this.destroyCalendar();
    const data = await this.ptReservation.getMenuAll();
    this.menuItems = data.map((d: any) => ({
      ...d,
      incTaxAmount: Math.floor((d.product?.amount ?? 0) * (1 * (d.product?.taxRate?.rate ?? 0) + 1)),
    }));
  }

  async selectMenu(menu: any): Promise<void> {
    const { studioId: id, timePerSlot: slot } = menu;
    const data = await this.ptReservation.getAvailability(id, slot, 1, 1) ?? [];
    this.modalControl = { isSelectTime: true };
    this.cdr.detectChanges();
    await this.waitForCalendarLayout();

    const calendarEl = this.calendarElement?.nativeElement;
    if (!calendarEl) return;
    this.destroyCalendar();
    calendarEl.style.display = 'none';
    calendarEl.innerHTML = '';

    this.calendar = new Calendar(calendarEl, {
      plugins: [jstCalendarPlugin, timeGridPlugin, dayGridPlugin, interactionPlugin],
      timeZone: 'Asia/Tokyo', now: calendarNow,
      locale: jaLocale,
      contentHeight: 'auto',
      initialView: 'timeGridWeek',
      slotMinTime: '09:00:00',
      slotMaxTime: '21:15:00',
      slotEventOverlap: false,
      slotLabelInterval: '00:15:00',
      dayMaxEventRows: 3,
      eventDisplay: 'block',
      titleFormat: { year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' },
      slotDuration: '00:15:00',
      slotLabelFormat: { hour12: false, hour: '2-digit', minute: '2-digit', omitZeroMinute: false, meridiem: false },
      displayEventEnd: false,
      eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
      eventMinHeight: 15,
      allDaySlot: false,
      headerToolbar: { end: 'today prev,next' },
      validRange: {
        start: businessDayjs().format('YYYY-MM-DD'),
        end: businessDayjs().add(30, 'day').format('YYYY-MM-DD'),
      },
      events: data,
      eventDataTransform: (eventData: any) => {
        if (eventData?.extendedProps?.type === 'holiday') return eventData;
        if (eventData?.url) {
          return {
            ...eventData,
            allDay: false,
            start: businessDayjs(eventData.start).hour(0).minute(0).second(0).toISOString(),
            end: businessDayjs(eventData.start).hour(24).minute(0).second(0).toISOString(),
          };
        }
        return { ...eventData, menu };
      },
      eventDidMount: (info: any) => {
        if (info.event?._def?.extendedProps?.type === 'holiday') return;
        if (info.event?._def?.url) return;
        if (info.event?.id) {
          info.el.style.borderColor = '#4EBCC4';
          info.el.style.backgroundColor = '#4EBCC4';
        } else {
          info.el.style.borderColor = '#FFDAB3';
          info.el.style.backgroundColor = '#FFDAB3';
        }
      },
      eventContent: (info: any) => {
        if (info.event?._def?.extendedProps?.type === 'holiday') return;
        return {
          html: `<div>
            <span class="calender-time-text">${info.timeText}</span>
            <div class="text-center">
              <span class="material-symbols-outlined" style="font-size: 1rem">${info.event.title}</span>
            </div>
          </div>`,
        };
      },
      datesSet: (info: any) => {
        void this.refreshHolidayEvents(info.start, info.end);
        const header = document.querySelector('.fc-header-toolbar') as HTMLElement;
        if (header && !header.querySelector('.fc-custom-notice')) {
          const backButtonWrap = document.createElement('div');
          backButtonWrap.style.marginLeft = 'auto';
          const backButton = document.createElement('div');
          backButton.className = 'btn btn-outline-secondary';
          backButton.setAttribute('type', 'button');
          backButton.addEventListener('click', () => this.modalBack());
          const span1 = document.createElement('span');
          span1.className = 'material-symbols-outlined align-bottom';
          span1.textContent = 'undo';
          const span2 = document.createElement('span');
          span2.innerHTML = '&nbsp;戻る';
          backButton.append(span1, span2);
          backButtonWrap.appendChild(backButton);
          header.prepend(backButtonWrap);

          const notice = document.createElement('div');
          notice.className = 'fc-custom-notice';
          notice.textContent = '当日のご予約はお電話にてお問い合わせください。';
          notice.style.fontSize = '14px';
          notice.style.marginTop = '4px';
          header.appendChild(notice);
        }
      },
      eventClick: (info: any) => {
        if (info.event?._def?.extendedProps?.type === 'holiday') return;
        if (!info.event?.id) return;
        this.selectReservation = {};
        const values = JSON.parse(JSON.stringify(info.event._def.extendedProps)).menu;
        const taxRate = values.product?.taxRate?.rate || 0;
        const amount = Math.floor((values.product?.amount || 0) * (1 * taxRate + 1));
        this.selectReservation = {
          reservationMenuId: values.id,
          studioId: values.studioId,
          timePerSlot: values.timePerSlot,
          requiredTime: values.requiredTime,
          menuName: values.menuName,
          taxRate,
          amount,
          taxAmount: amount - (values.product?.amount || 0),
          startedAt: info.event.id,
          displayStartedAt: businessDayjs(info.event.id).format('YYYY/MM/DD(ddd) HH:mm'),
          displayEndedAt: businessDayjs(info.event.id).add(values.requiredTime, 'minute').format('YYYY/MM/DD(ddd) HH:mm'),
        };
        this.modalControl = { isConfirm: true };
        const modalEl = document.getElementById('newReservationModal');
        if (modalEl) modalEl.scrollTop = 0;
      },
    });

    this.calendar.render();
    calendarEl.style.display = 'block';
    await this.waitForCalendarLayout();
    this.calendar.updateSize();
    const modalEl = document.getElementById('newReservationModal');
    if (modalEl) modalEl.scrollTop = 0;
  }

  modalBack(): void {
    const modalEl = document.getElementById('newReservationModal');
    if (modalEl) modalEl.scrollTop = 0;
    if (this.modalControl.isSelectTime) {
      this.modalControl = { isSelectMenu: true };
    } else if (this.modalControl.isConfirm) {
      this.modalControl = { isSelectTime: true };
    }
  }

  setDetail(item: any): void {
    this.modalControl = { showDetail: true };
    this.selectReservation = JSON.parse(JSON.stringify(item));
    this.selectReservation.displayStartedAt = businessDayjs(this.selectReservation.startedAt).format('YYYY/MM/DD(ddd) HH:mm');
    this.selectReservation.displayEndedAt = businessDayjs(this.selectReservation.endedAt).format('YYYY/MM/DD(ddd) HH:mm');
    this.selectReservation.displayCreatedAt = businessDayjs(this.selectReservation.createdAt).format('YYYY/MM/DD(ddd) HH:mm:ss');
    this.selectReservation.isRemovable = this.isRemovable(this.selectReservation);
  }

  showConfirm(): void {
    this.modalControl = { showConfirm: true };
  }

  async save(): Promise<void> {
    if (!this.selectReservation || String(this.selectReservation.note ?? '').length > 255) return;
    if (!await this.businessClock.confirm(this.clockContext)) return;
    const startedAt = businessDayjs(this.selectReservation.startedAt);
    const today = businessDayjs().startOf('day');
    if (!startedAt.isAfter(today.endOf('day')) || !startedAt.isBefore(today.add(30, 'day'))) {
      this.toastr.warning('予約日時を選び直してください。');
      return;
    }
    const result = await this.ptReservation.create(this.selectReservation);
    if (!result.success) {
      this.toastr.error(result.error || 'エラーが発生しました。(DF)');
    } else {
      this.toastr.success('予約が完了しました。');
      await this.update();
    }
  }

  async remove(): Promise<void> {
    if (!await this.businessClock.confirm(this.clockContext)) return;
    const data = await this.ptReservation.get(this.selectReservation.id);
    if (data && this.isRemovable(data)) {
      const result = await this.ptReservation.remove(this.selectReservation);
      if (!result.success) {
        this.toastr.error(result.error || 'キャンセルエラーが発生しました。(DF)');
      } else {
        this.toastr.success('予約をキャンセルしました。');
        await this.update();
      }
    } else if (data?.status === 3) {
      this.toastr.warning('キャンセル済です。');
      await this.update();
    } else {
      this.toastr.error('キャンセルエラーが発生しました。(DL)');
      await this.update();
    }
  }

  private isRemovable(reservation: any): boolean {
    return (reservation.status === 0 || reservation.status === 1)
      && businessDayjs(reservation.startedAt).isSameOrAfter(businessDayjs().add(1, 'day'), 'day');
  }

  private destroyCalendar(): void {
    this.holidayLoadId++;
    if (!this.calendar) return;
    this.calendar.destroy();
    this.calendar = null;
  }

  private async refreshHolidayEvents(start: Date, end: Date): Promise<void> {
    if (!this.calendar) return;
    const loadId = ++this.holidayLoadId;
    try {
      const events = await loadJapaneseHolidayEvents(start, end);
      if (!this.calendar || loadId !== this.holidayLoadId) return;
      this.calendar.getEventSourceById(JAPANESE_HOLIDAY_SOURCE_ID)?.remove();
      this.calendar.addEventSource({ id: JAPANESE_HOLIDAY_SOURCE_ID, events });
    } catch (error) {
      console.warn('日本祝日カレンダーの取得に失敗しました。', error);
    }
  }

  private waitForCalendarLayout(): Promise<void> {
    return new Promise(resolve => requestAnimationFrame(() => resolve()));
  }

  formatCurrency(val: number): string {
    return `¥${val?.toLocaleString('ja-JP') ?? 0}`;
  }
}
